import { ArrayResolution, Filter } from '@fabric-ui/components';
import { OptionQueryType } from '@fabric-ui/components/Table';
import { and, field, or } from '@utils/filter/filter-builder';
import { mapFilterLeaves } from '@utils/filter/map-filter-leaves';
import { removeByField } from '@utils/filter/remove-filter-field';
import { wrapArrayCondition } from '@utils/filter/wrap-array-condition';
import { filterToExpression } from '@utils/filterUtils';
import { gql } from 'graphql-request';
import { ReportsTypes, SharingFieldValue } from './types';

export const SHARING_FILTER_FIELDS = ['sharing.type', 'sharing.user.id', 'sharing.ps.id', 'sharing.label'] as const;

export type SharingOptionIdentity = [SharingFieldValue['kind'], string];

const sharingFieldByKind: Record<SharingFieldValue['kind'], (typeof SHARING_FILTER_FIELDS)[number]> = {
  SHARING_TYPE: 'sharing.type',
  USER: 'sharing.user.id',
  PERMISSION_SET: 'sharing.ps.id',
};

export const encodeSharingOption = ({ kind, value }: SharingFieldValue) => JSON.stringify([kind, value] satisfies SharingOptionIdentity);

export const decodeSharingOption = (encoded: string): SharingOptionIdentity | null => {
  try {
    const value = JSON.parse(encoded);
    if (!Array.isArray(value) || value.length !== 2 || !Object.hasOwn(sharingFieldByKind, value[0]) || typeof value[1] !== 'string') {
      return null;
    }
    return value as SharingOptionIdentity;
  } catch {
    return null;
  }
};

export const sharingSelectionsToFilter = (values: unknown): Filter | null => {
  if (!Array.isArray(values)) return null;

  const grouped = values.reduce<Partial<Record<SharingFieldValue['kind'], string[]>>>((result, encoded) => {
    const identity = typeof encoded === 'string' ? decodeSharingOption(encoded) : null;
    if (!identity) return result;
    const [kind, value] = identity;
    return { ...result, [kind]: [...(result[kind] ?? []), value] };
  }, {});
  const operands = Object.entries(grouped).map(([kind, selected]) =>
    field(sharingFieldByKind[kind as SharingFieldValue['kind']]).str.in(selected)
  );

  if (!operands.length) return null;
  return operands.length === 1 ? operands[0] : or(...operands);
};

export const withoutSharingFilters = (filter?: Filter | null): Filter | null =>
  SHARING_FILTER_FIELDS.reduce<Filter | null>((result, fieldName) => (result ? removeByField(result, fieldName) : null), filter ?? null);

const EMPTY_FILTER_VALUE = '__DASHBOARD_REPORT_EMPTY__';

const wrapTagConditions = (source: Filter): Filter =>
  mapFilterLeaves(source, leaf => {
    const { expression, ...metadata } = leaf;
    const condition = expression.stringCondition;
    if (condition?.empty) return leaf;
    if (condition?.notEqual !== undefined) {
      return { ...metadata, not: wrapArrayCondition(field(expression.fieldName!).str.equals(condition.notEqual), ArrayResolution.ANY) };
    }
    if (condition?.notContains !== undefined) {
      return {
        ...metadata,
        not: wrapArrayCondition(field(expression.fieldName!).str.contains(condition.notContains), ArrayResolution.ANY),
      };
    }
    if (condition?.notIn) {
      return { ...metadata, not: wrapArrayCondition(field(expression.fieldName!).str.in(condition.notIn.values), ArrayResolution.ANY) };
    }
    return wrapArrayCondition(leaf, ArrayResolution.ANY);
  });

export const tableFiltersToDashboardReportFilter = (
  typeMap: Record<string, unknown>,
  filters: Record<string, unknown>
): Filter | undefined => {
  const nonEmptyFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => (Array.isArray(value) ? value.length > 0 : value !== '' && value != null))
  );
  const { sharing, tags, ...otherFilters } = nonEmptyFilters;
  const { regularFilters, emptyFilters } = Object.entries(otherFilters).reduce(
    (result, [fieldName, value]) => {
      if (!Array.isArray(value) || !value.includes(EMPTY_FILTER_VALUE)) {
        return { ...result, regularFilters: { ...result.regularFilters, [fieldName]: value } };
      }

      const remainingValues = value.filter(item => item !== EMPTY_FILTER_VALUE);
      const remainingFilter = remainingValues.length ? filterToExpression(typeMap, { [fieldName]: remainingValues }, []) : undefined;
      const remainingOperands = remainingFilter?.and?.operands ?? [];
      const emptyFilter = field(fieldName).str.empty();
      return {
        regularFilters: result.regularFilters,
        emptyFilters: [...result.emptyFilters, remainingOperands.length ? or(...remainingOperands, emptyFilter) : emptyFilter],
      };
    },
    { regularFilters: {} as Record<string, unknown>, emptyFilters: [] as Filter[] }
  );
  const sharingValues = Array.isArray(sharing) ? sharing : [];
  const labelConditions = sharingValues.filter(value => {
    if (typeof value !== 'string' || decodeSharingOption(value)) return false;
    try {
      const parsed = JSON.parse(decodeURIComponent(value));
      return !!parsed && typeof parsed === 'object' && !Array.isArray(parsed);
    } catch {
      return false;
    }
  });
  const genericFilter = filterToExpression(typeMap, {
    ...regularFilters,
    ...(labelConditions.length ? { 'sharing.label': labelConditions } : {}),
  });
  const genericOperands = genericFilter.and?.operands ?? [];
  const tagValues = Array.isArray(tags) ? tags : [];
  const hasEmptyTag = tagValues.includes(EMPTY_FILTER_VALUE);
  const nonEmptyTagValues = tagValues.filter(value => value !== EMPTY_FILTER_VALUE);
  const scalarTagsFilter = nonEmptyTagValues.length ? filterToExpression(typeMap, { tags: nonEmptyTagValues }, []) : undefined;
  const tagsFilter = scalarTagsFilter?.and?.operands.length ? wrapTagConditions(scalarTagsFilter) : undefined;
  const emptyTagsFilter = hasEmptyTag ? field('tags').str.empty() : undefined;
  const combinedTagsFilter = tagsFilter && emptyTagsFilter ? or(tagsFilter, emptyTagsFilter) : (tagsFilter ?? emptyTagsFilter);
  const sharingFilter = sharingSelectionsToFilter(sharingValues);
  const operands = [
    ...genericOperands,
    ...emptyFilters,
    ...(combinedTagsFilter ? [combinedTagsFilter] : []),
    ...(sharingFilter ? [sharingFilter] : []),
  ];
  return operands.length ? and(...operands) : undefined;
};

export const normalizeDashboardReportFilterOptions = (
  options: Array<{ title: string; value: unknown }>
): Array<{ title: string; value: unknown }> =>
  options.map(option => (option.value === null || option.value === '' ? { title: 'Empty', value: EMPTY_FILTER_VALUE } : option));

export const FIND_DASHBOARD_REPORT_FIELD_VALUES = gql`
  query findDashboardReportFieldValues($fieldName: String, $filterablePage: FilterablePage) {
    findDashboardReportFieldValues(fieldName: $fieldName, filterablePage: $filterablePage) {
      content
      totalElements
      totalPages
      number
      size
      first
      last
      empty
    }
  }
`;

export const buildDashboardReportsFilter = ({
  type,
  selectedAppName,
  selectedAppIsExplore,
  predefined,
  tableFilter,
}: {
  type: ReportsTypes;
  selectedAppName?: string;
  selectedAppIsExplore: boolean;
  predefined?: boolean;
  tableFilter?: Filter;
}): Filter => {
  const operands: Filter[] = [
    field('type').str.equals(type),
    ...(!selectedAppIsExplore && selectedAppName ? [field('apps').str.equals(selectedAppName)] : []),
    ...(predefined === undefined ? [] : [field('predefined').bool.equals(predefined)]),
    ...(tableFilter ? [tableFilter] : []),
  ];
  return and(...operands);
};

// Candidates for the "pin to app" dialog: reports not already pinned to the app and not predefined.
export const buildPinToAppReportsFilter = ({
  type,
  selectedAppName,
  tableFilter,
}: {
  type: ReportsTypes;
  selectedAppName: string;
  tableFilter?: Filter;
}): Filter =>
  and(
    field('type').str.equals(type),
    field('apps').str.notContains(selectedAppName),
    field('predefined').bool.equals(false),
    ...(tableFilter ? [tableFilter] : [])
  );

export const createDashboardReportAsyncOptions =
  ({
    type,
    selectedAppName,
    selectedAppIsExplore,
    predefined,
    tableFilter,
    getOptionLabel,
  }: {
    type: ReportsTypes;
    selectedAppName?: string;
    selectedAppIsExplore: boolean;
    predefined?: boolean;
    tableFilter?: Filter;
    getOptionLabel?: (fieldName: string, value: string) => string | undefined;
  }) =>
  (fieldName: string) =>
  (search: string) => {
    const cohortFilter = buildDashboardReportsFilter({
      type,
      selectedAppName,
      selectedAppIsExplore,
      predefined,
      tableFilter: tableFilter ? (removeByField(tableFilter, fieldName) ?? undefined) : undefined,
    });
    const filter = and(...(cohortFilter.and?.operands ?? []), ...(search ? [field(fieldName).str.contains(search)] : []));
    return {
      type: OptionQueryType.GQL_PAGINATION,
      query: FIND_DASHBOARD_REPORT_FIELD_VALUES,
      queryKey: ['dashboardReportFieldValues', type, fieldName, selectedAppName, selectedAppIsExplore, predefined, filter],
      variables: {
        fieldName,
        filterablePage: {
          filter,
          pageable: { pageNumber: 0, pageSize: 100, sortBy: fieldName, order: 'asc' },
        },
      },
      select: data => {
        const options = ((data?.content ?? []) as string[]).map(value => ({
          title: getOptionLabel?.(fieldName, value) ?? value,
          value,
        }));
        return { data: options, totals: data?.totalElements ?? options.length };
      },
    };
  };

// TODO: divide sharing options into groups (State/PS/User) once TableV2's list filter exposes groupByFunc; unsupported today.
export const createSharingAsyncOptions = ({
  type,
  selectedAppName,
  selectedAppIsExplore,
  predefined,
  tableFilter,
  includePermissionSets,
  optionByValue,
}: {
  type: ReportsTypes;
  selectedAppName?: string;
  selectedAppIsExplore: boolean;
  predefined?: boolean;
  tableFilter?: Filter;
  includePermissionSets: boolean;
  optionByValue: Map<string, SharingFieldValue>;
}) => {
  const cohortFilter = buildDashboardReportsFilter({
    type,
    selectedAppName,
    selectedAppIsExplore,
    predefined,
    tableFilter: withoutSharingFilters(tableFilter) ?? undefined,
  });

  return {
    asyncGetOptions: (search: string) => {
      const filter = and(...(cohortFilter.and?.operands ?? []), ...(search ? [field('sharing').str.contains(search)] : []));
      return {
        type: OptionQueryType.GQL_PAGINATION,
        query: FIND_DASHBOARD_REPORT_FIELD_VALUES,
        queryKey: ['dashboardReportFieldValues', type, selectedAppName, selectedAppIsExplore, predefined, filter],
        variables: {
          fieldName: 'sharing',
          filterablePage: {
            filter,
            pageable: { pageNumber: 0, pageSize: 100, sortBy: 'sharing', order: 'asc' },
          },
        },
        select: data => {
          const content = ((data?.content ?? []) as SharingFieldValue[]).filter(
            option =>
              (includePermissionSets || option.kind !== 'PERMISSION_SET') && !(option.kind === 'SHARING_TYPE' && option.value === 'SHARED')
          );
          const options = content.map(option => {
            const value = encodeSharingOption(option);
            optionByValue.set(value, option);
            return { title: option.label, value };
          });
          return { data: options, totals: data?.totalElements ?? options.length };
        },
      };
    },
    getOptions: options =>
      options.map(option => ({
        ...option,
        title: optionByValue.get(option.value)?.label ?? option.title,
      })),
  };
};
